import { useEffect } from 'react'
import { Button, Card, Spinner, Text, Title } from '@nimbus-ds/components'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'

export function LoginPage() {
  const { loginWithGoogle, installStoreViaTiendaNube, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()
  const showInstallStore = searchParams.get('installStore') === '1'

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  // Handle OAuth callback errors
  useEffect(() => {
    const error = searchParams.get('error')
    const errorMessage = searchParams.get('message') || searchParams.get('detail')

    if (error) {
      console.error('OAuth error:', error, errorMessage)

      let title = 'Error de autenticación'
      let description = 'Ha ocurrido un error durante el proceso de autenticación.'

      if (error === 'domain_not_allowed') {
        title = 'Dominio no autorizado'
        description = 'Solo cuentas @ecommercelab.agency pueden iniciar sesión. Si necesitas acceso, contacta al administrador.'
      } else if (error === 'email_not_verified') {
        title = 'Email no verificado'
        description = 'Tu cuenta de Google no tiene el email verificado.'
      } else if (error === 'callback_failed' || error === 'oauth_error' || error === 'missing_code') {
        title = 'Inicio de sesión fallido'
        description = 'No pudimos completar el inicio de sesión con Google. Inténtalo nuevamente.'
      } else if (error === 'access_denied') {
        title = 'Acceso denegado'
        description = 'Has cancelado el proceso de autorización. Por favor, inténtalo de nuevo.'
      } else if (error === 'invalid_client') {
        title = 'Configuración inválida'
        description = 'La configuración de la aplicación es inválida. Por favor, contacta al soporte.'
      } else if (error === 'invalid_grant') {
        title = 'Sesión expirada'
        description = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
      } else if (errorMessage) {
        description = errorMessage
      }

      toast({
        title,
        description,
        variant: 'destructive',
        duration: 6000,
      })
    }
  }, [searchParams])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="large" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--nimbus-colors-primary-surface)] to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and branding */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-6 flex justify-center">
            <img
              src="/logo.svg"
              alt="ECommerceLab"
              className="h-12 w-auto"
            />
          </div>
          <Title as="h1">ECommerceLab Desk</Title>
          <Text>Plataforma CRO para TiendaNube</Text>
        </div>

        {/* Login card */}
        <Card>
          <Card.Header>
            <div className="text-center space-y-1">
              <Title as="h2">Bienvenido</Title>
              <Text>Inicia sesión con tu cuenta @ecommercelab.agency</Text>
            </div>
          </Card.Header>
          <Card.Body>
          <div className="space-y-4">
            <Button
              onClick={loginWithGoogle}
              appearance="neutral"
              fullWidth
            >
              <svg
                className="w-5 h-5 mr-2"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.997 10.997 0 0 0 12 23z" fill="#34A853" />
                <path d="M5.84 14.09A6.6 6.6 0 0 1 5.47 12c0-.73.13-1.43.36-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335" />
              </svg>
              Iniciar sesión con Google
            </Button>

            {showInstallStore && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">Instalar una tienda</span>
                  </div>
                </div>

                <Button
                  onClick={installStoreViaTiendaNube}
                  appearance="neutral"
                  fullWidth
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2L2 7v10c0 5.55 3.84 9.95 9 11 5.16-1.05 9-5.45 9-11V7l-10-5z"/>
                  </svg>
                  Conectar tienda con TiendaNube
                </Button>
              </>
            )}

            <div className="text-center space-y-2">
              <Text fontSize="caption">
                Al iniciar sesión, aceptas nuestros{' '}
                <a href="#" className="text-blue-600 hover:underline">Términos</a>
                {' '}y{' '}
                <a href="#" className="text-blue-600 hover:underline">Política de privacidad</a>
              </Text>
            </div>
          </div>
          </Card.Body>
        </Card>

        {/* Features preview */}
        <div className="mt-8 text-center">
          <Title as="h3" className="mb-4">
            Optimize Your TiendaNube Store
          </Title>
          <div className="grid grid-cols-1 gap-3 text-sm text-gray-600">
            <div className="flex items-center justify-center">
              <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
              A/B test product pages, checkout flow, and more
            </div>
            <div className="flex items-center justify-center">
              <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
              Real-time analytics and conversion tracking
            </div>
            <div className="flex items-center justify-center">
              <div className="w-2 h-2 bg-purple-600 rounded-full mr-3"></div>
              Easy setup with your existing TiendaNube store
            </div>
          </div>
        </div>

        {/* Help link */}
        <div className="mt-8 text-center">
          <a
            href="https://ecommercelab.agency/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            ¿Necesitas ayuda? Contacta soporte
          </a>
        </div>
      </div>
    </div>
  )
}